import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Play, Grid3x3, Users } from 'lucide-react'
import evaluationGridService from '../../../services/evaluationGridService'
import classService from '../../../services/classService'

export function EvaluationSessionSetupPage() {
  const navigate = useNavigate()
  const [selectedGridId, setSelectedGridId] = useState('')
  const [selectedClassId, setSelectedClassId] = useState('')

  const { data: grids, isLoading: loadingGrids } = useQuery({
    queryKey: ['evaluationGrids'],
    queryFn: () => evaluationGridService.getAll()
  })

  const { data: classes, isLoading: loadingClasses } = useQuery({
    queryKey: ['classes'],
    queryFn: () => classService.getAll()
  })

  const selectedGrid = grids?.find((g: any) => g.id === selectedGridId)
  const selectedClass = classes?.find((c: any) => c.id === selectedClassId)

  const handleStart = () => {
    if (selectedGridId && selectedClassId) {
      navigate(`/modules/evaluation/evaluate/${selectedGridId}/${selectedClassId}`)
    }
  }

  const canStart = selectedGridId && selectedClassId

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/modules/evaluation')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour au module</span>
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Nouvelle session d'évaluation</h1>
          <p className="text-gray-600">Sélectionnez une grille et une classe pour commencer l'évaluation</p>
        </div>

        <div className="space-y-6">
          {/* Sélection grille */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <Grid3x3 className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">1. Grille d'évaluation</h2>
                <p className="text-sm text-gray-600">Choisissez la grille à utiliser</p>
              </div>
            </div>

            {loadingGrids ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
              </div>
            ) : !grids || grids.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">Aucune grille disponible</p>
                <button
                  onClick={() => navigate('/modules/evaluation/grids/new')}
                  className="text-violet-600 hover:text-violet-700 font-medium"
                >
                  Créer une grille
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {grids.map((grid: any) => (
                  <button
                    key={grid.id}
                    onClick={() => setSelectedGridId(grid.id)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      selectedGridId === grid.id
                        ? 'border-violet-500 bg-violet-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{grid.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {grid.className} • {grid.academicYear} • {grid.semester}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {grid.criteria?.length || 0} critère{(grid.criteria?.length || 0) > 1 ? 's' : ''}
                        </p>
                      </div>
                      {selectedGridId === grid.id && (
                        <div className="w-6 h-6 rounded-full bg-violet-500 flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Sélection classe */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">2. Classe à évaluer</h2>
                <p className="text-sm text-gray-600">Sélectionnez la classe concernée</p>
              </div>
            </div>

            {loadingClasses ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : !classes || classes.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">Aucune classe disponible</p>
                <button
                  onClick={() => navigate('/classes')}
                  className="text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  Gérer les classes
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {classes.map((cls: any) => (
                  <button
                    key={cls.id}
                    onClick={() => setSelectedClassId(cls.id)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      selectedClassId === cls.id
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{cls.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {cls.studentCount || 0} élève{(cls.studentCount || 0) > 1 ? 's' : ''}
                        </p>
                      </div>
                      {selectedClassId === cls.id && (
                        <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Récapitulatif */}
          {canStart && (
            <div className="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-xl border border-violet-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Récapitulatif</h3>
              <div className="space-y-2 text-sm text-gray-700">
                <p><span className="font-medium">Grille :</span> {selectedGrid?.name}</p>
                <p><span className="font-medium">Classe :</span> {selectedClass?.name}</p>
                <p><span className="font-medium">Critères :</span> {selectedGrid?.criteria?.length || 0}</p>
                <p><span className="font-medium">Élèves :</span> {selectedClass?.studentCount || 0}</p>
              </div>
            </div>
          )}

          {/* Bouton de démarrage */}
          <button
            onClick={handleStart}
            disabled={!canStart}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl hover:from-violet-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-400"
          >
            <Play className="w-5 h-5" />
            <span>Démarrer l'évaluation</span>
          </button>
        </div>
      </div>
    </div>
  )
}
